import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Grid,
    CircularProgress,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';

import { t } from "../../constants/translationHelper";
import { EXPORT_SHIPMENT_COMPARE, IMPORT_SHIPMENT_COMPARE } from "../../services/apiRoutes";
import { genericPost } from "../../services/authServices";

const ExportCompareView = ({ handleClose, exportShipmentId }) => {

    const [data, setData] = useState(null);
    const [baseCurrency, setBaseCurrency] = useState("");
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    // format amount with currency
    const formatAmount = (amount) => {
        const value = amount || 0;
        return `${baseCurrency} ${value.toFixed(2)}`;
    };

    useEffect(() => {

        const getCompareData = async () => {

            if (!exportShipmentId) return;

            try {

                const payload = {
                    shipmentOrgId: exportShipmentId
                };
                const response = await genericPost(
                    EXPORT_SHIPMENT_COMPARE,
                    payload,
                    {}
                );
                setData(response.data.report);
                setBaseCurrency(response.data.baseCurrency || "");

            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        getCompareData();

    }, [exportShipmentId]);

    if (loading) return <CircularProgress />;

    if (error) {
        return (
            <Typography color="error">
                Error: {error}
            </Typography>
        );
    }

    return (
        <Box sx={{ mx: 'auto', mt: 0 }}>

            {/* Header */}
            <Grid container spacing={1} style={{ position: "sticky" }}>

                <Grid item xs={4}>
                    <Typography
                        sx={{ fontWeight: 600 }}
                        style={{ fontSize: "18px", paddingTop: "5px" }}
                    >
                        {t("Export Shipment Compare")}
                    </Typography>
                </Grid>

                <Grid item xs={8} style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Box>

                        <IconButton aria-label="print">
                            <LocalPrintshopOutlinedIcon
                                sx={{ fontSize: '20px', color: '#007bff' }}
                            />
                        </IconButton>

                        <IconButton aria-label="close">
                            <CloseIcon
                                sx={{ fontSize: '20px', color: '#007bff' }}
                                onClick={handleClose}
                            />
                        </IconButton>

                    </Box>
                </Grid>

            </Grid>

            <Divider />

            {/* Table */}
            <TableContainer sx={{ mt: 2 }}>

                <Table sx={{ minWidth: 650 }}>

                    <TableHead sx={{ height: '40px', backgroundColor: '#e7f0f7' }}>
                        <TableRow>

                            <TableCell sx={{ fontSize: '16px' }}>
                                {t("Category")}
                            </TableCell>

                            <TableCell align="right" sx={{ fontSize: '16px' }}>
                                {t("Booking")}
                            </TableCell>

                            <TableCell align="right" sx={{ fontSize: '16px' }}>
                                {t("Planning")}
                            </TableCell>

                            <TableCell align="right" sx={{ fontSize: '16px' }}>
                                {t("Difference")}
                            </TableCell>

                        </TableRow>
                    </TableHead>

                    <TableBody>

                        {data && Object.entries(data).map(([category, values]) => {

                            let bookingValue = 0;

                            if (category === "duty amount") {
                                bookingValue = values.dutyParticularAmount;
                            }
                            else if (category === "tax amount") {
                                bookingValue = values.taxParticularAmount;
                            }
                            else {
                                bookingValue = values.booking;
                            }

                            return (
                                <TableRow
                                    key={category}
                                    sx={{
                                        '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                                        '&:hover': { backgroundColor: '#e3f2fd' }
                                    }}
                                >

                                    <TableCell
                                        component="th"
                                        scope="row"
                                        sx={{ fontWeight: 'medium', textTransform: 'capitalize' }}
                                    >
                                        {category}
                                    </TableCell>

                                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                        {formatAmount(bookingValue)}
                                    </TableCell>

                                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                        {formatAmount(values.planning)}
                                    </TableCell>

                                    <TableCell
                                        align="right"
                                        sx={{
                                            fontFamily: 'monospace',
                                            color: (values.difference || 0) < 0 ? 'red' : 'green'
                                        }}
                                    >
                                        {formatAmount(values.difference)}
                                    </TableCell>

                                </TableRow>
                            );

                        })}

                    </TableBody>

                </Table>

            </TableContainer>

        </Box>
    );
};

export default ExportCompareView;